import i18n from '@/locales/i18n';
import { ref } from 'vue';
import { useStore } from 'vuex';
import { refreshAzureTokenApi } from '@/api/permission';
import { message } from '@koi-design/uxd-ui';
import { notifyAccountStatusChange } from '@/libs/accountSync';

// Constant, refresh token before expiry minutes
const REFRESH_TOKEN_BEFORE_EXPIRY_MINUTES = 3;
export function useAzureAuth() {
  const store = useStore();
  const refreshTimer = ref(null);
  const isRefreshing = ref(false);

  // Check if it is Azure authentication
  const isAzureAuth = () => {
    return localStorage.getItem('auth_provider') === 'tp_azure';
  };

  // Get token expiry time
  const getTokenExpiryTime = () => {
    const userInfo = store.getters.botUserInfo;
    if (!userInfo.expires_in) return null;

    // Calculate token expiry timestamp
    const tokenCreatedTime = localStorage.getItem('azure_token_created_time');
    if (!tokenCreatedTime) return null;
    return parseInt(tokenCreatedTime) + userInfo.expires_in * 1000;
  };

  // Refresh token
  async function refreshToken() {
    if (isRefreshing.value) return;
    try {
      isRefreshing.value = true;
      const userInfo = store.getters.botUserInfo;
      if (!userInfo.refresh_token) {
        console.warn('No refresh token available');
        return;
      }
      const response = await refreshAzureTokenApi(
        userInfo.token,
        userInfo.refresh_token
      );

      // Update user information
      const updatedUserInfo = {
        ...userInfo,
        token: response.access_token,
        refresh_token: response.refresh_token,
        expires_in: response.expires_in
      };
      store.commit('setUserInfo', updatedUserInfo);
      localStorage.setItem('sbtToken', response.access_token);
      localStorage.setItem('azure_token_created_time', Date.now().toString());
      console.log('Azure token refreshed successfully');

      // Reset timer
      setupTokenRefreshTimer();
    } catch (error) {
      console.error('Failed to refresh Azure token:', error);
      message.error(
        i18n.global.t('hooks.useAzureAuth.757c84d94776154f2657714470ec888c')
      );

      // Clear authentication information and redirect to login page
      notifyAccountStatusChange('session-expired');
      localStorage.removeItem('sbtToken');
      localStorage.removeItem('auth_provider');
      localStorage.removeItem('azure_token_created_time');
      store.commit('setUserInfo', {});
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);
    } finally {
      isRefreshing.value = false;
    }
  }

  // Set token refresh timer
  function setupTokenRefreshTimer() {
    // Clear existing timer
    if (refreshTimer.value) {
      clearTimeout(refreshTimer.value);
      refreshTimer.value = null;
    }
    if (!isAzureAuth()) return;
    const expiryTime = getTokenExpiryTime();
    if (!expiryTime) return;
    const now = Date.now();
    const timeUntilExpiry = expiryTime - now;

    // Refresh token at specified time before expiry
    const refreshTime = Math.max(
      timeUntilExpiry - REFRESH_TOKEN_BEFORE_EXPIRY_MINUTES * 60 * 1000,
      0
    );
    if (refreshTime > 0) {
      refreshTimer.value = setTimeout(() => {
        refreshToken();
      }, refreshTime);
      console.log(
        `Azure token will be refreshed in ${refreshTime / 1000} seconds`
      );
    } else {
      // If it is approaching expiry, refresh immediately
      refreshToken();
    }
  }

  // Initialize Azure authentication
  const initAzureAuth = () => {
    console.log('initAzureAuth');
    if (!isAzureAuth()) return;
    const userInfo = store.getters.botUserInfo;
    if (!userInfo.token || !userInfo.refresh_token) return;

    // If there is no token creation time, set it to the current time
    if (!localStorage.getItem('azure_token_created_time')) {
      localStorage.setItem('azure_token_created_time', Date.now().toString());
    }
    setupTokenRefreshTimer();
  };

  // Clean up timer
  const cleanup = () => {
    if (refreshTimer.value) {
      console.log('cleanup');
      clearTimeout(refreshTimer.value);
      refreshTimer.value = null;
    }
  };
  return {
    isAzureAuth,
    refreshToken,
    setupTokenRefreshTimer,
    initAzureAuth,
    cleanup
  };
}
